import { FormattedMessage } from 'react-intl';
import { toastr } from 'react-redux-toastr';

import ConvertToApplicantModal from '../../../containers/ConvertToApplicantModal';
// eslint-disable-next-line max-len
import { MODAL_MODES } from '../../../containers/ConvertToApplicantModal/constants';
import useHasPermission from '../../../hooks/useHasPermission';
import useIntl from '../../../hooks/useIntl';
import { WAITLIST_ACTION_SCOPE } from '../constants';
import { useAddToWaitlist } from '../hooks';
import messages from '../messages';

// Adding an existing applicant is the same review the agent does when editing
// or reinstating: the wizard opens on the application's own data, the agent
// fills in the waitlist answers nobody has given yet, and saving creates the
// waitlist record and its positions.
//
// Deliberately unlike ReinstateFromWaitlist: no reloadPage on a 400. The
// likeliest refusal here is ticking no waitlist program, where the backend
// saves nothing at all -- reloading would discard a filled-in wizard in
// answer to a validation error. The modal stays open with the message.
const AddToWaitlist = ({
  organizationId,
  propertyId,
  applicationId,
  floorPlans,
  open,
  handleClose,
  onSuccess,
}) => {
  const intl = useIntl();
  const { mutate: addMutate, isLoading: isSubmitting } = useAddToWaitlist({
    organizationId,
    propertyId,
    options: {
      onSuccess: (response) => {
        const movedToInProcess = Boolean(response?.movedToInProcess);
        toastr.success(
          'Success',
          intl.formatMessage(
            movedToInProcess
              ? messages.addMovedToInProcessSuccess
              : messages.addSuccess,
          ),
        );
        // handleClose means "the user dismissed me"; callers close the modal
        // from onSuccess, same contract as the sibling actions.
        //
        // The status-dropdown caller matches this against the action it still has pending, so a
        // late success from a modal the agent already replaced is ignored. The card caller takes
        // no arguments and is unaffected. No affordableWaitlistId: the caller had none to match
        // against, and a selection with no Waitlist Program from New Portal App creates no
        // record at all. That case moves to In Process instead, so the caller acts on
        // movedToInProcess.
        onSuccess({ action: 'add', movedToInProcess });
      },
      onError: (e) => toastr.error('Error', e?.message ?? e),
    },
  });

  // A dismissed modal keeps its mutation in flight, so block dismissal while
  // the request is pending rather than hand a caller a success for a form the
  // agent already walked away from.
  const handleDismiss = () => {
    if (isSubmitting) return;
    handleClose();
  };

  const canAct = useHasPermission(WAITLIST_ACTION_SCOPE);

  if (!open) return null;

  return (
    <ConvertToApplicantModal
      open
      mode={MODAL_MODES.ADD}
      applicationId={applicationId}
      floorPlans={floorPlans}
      onClose={handleDismiss}
      onSubmit={(payload) => addMutate({ applicationId, payload })}
      isSubmitting={isSubmitting}
      submitDisabledTooltip={
        canAct ? undefined : (
          <FormattedMessage {...messages.permissionRequired} />
        )
      }
    />
  );
};

export default AddToWaitlist;
